import { useMemo, useState } from 'react'
import Select, { SingleValue } from 'react-select'
import ReactApexChart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'

type Option = { value: string; label: string }

const ProfilingPage = () => {
  const [selectedProfile, setSelectedProfile] = useState<Option | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [eventsFile, setEventsFile] = useState<File | null>(null)

  const profileOptions = useMemo<Option[]>(
    () => [
      { value: 'baseline-q1', label: 'Baseline Q1' },
      { value: 'baseline-q2', label: 'Baseline Q2' },
      { value: 'summer-peak', label: 'Summer Peak' },
    ],
    []
  )

  const handleDownloadEventsTemplate = () => {
    const csvContent = [
      'event_name,event_date,event_weight',
      'Holiday,2025-01-01,1.0',
      'Maintenance,2025-02-10,0.7',
    ].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'events_template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleGenerateProfile = () => {
    // dummy: close modal and simulate profile selection
    setIsModalOpen(false)
    setSelectedProfile({
      value: `custom-${Date.now()}`,
      label: `Snapshot ${startDate} → ${endDate}`,
    })
  }

  const kpis = useMemo(
    () => [
      { title: 'Avg Daily Consumption', value: '132 kWh', change: '+2.1%' },
      { title: 'Peak Hour', value: '14:30', change: 'Weekdays' },
      { title: 'Seasonal Adj.', value: '+6.5%', change: 'Summer' },
    ],
    []
  )

  const weeklyOptions: ApexOptions = useMemo(
    () => ({
      chart: { type: 'line', toolbar: { show: false } },
      stroke: { curve: 'smooth', width: 3 },
      dataLabels: { enabled: false },
      xaxis: { categories: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] },
      yaxis: { labels: { formatter: (v) => `${v} kWh` } },
      colors: ['#3E97FF'],
      legend: { show: false },
    }),
    []
  )
  const weeklySeries = useMemo(
    () => [{ name: 'Consumption', data: [120, 130, 125, 140, 150, 160, 110] }],
    []
  )

  const seasonalDonutOptions: ApexOptions = useMemo(
    () => ({
      chart: { type: 'donut' },
      labels: ['Winter', 'Spring', 'Summer', 'Autumn'],
      legend: { position: 'bottom' },
      dataLabels: { enabled: false },
      colors: ['#50CD89', '#7239EA', '#FFC700', '#F1416C'],
    }),
    []
  )
  const seasonalDonutSeries = useMemo(() => [25, 20, 35, 20], [])

  const heatmapOptions: ApexOptions = useMemo(
    () => ({
      chart: { type: 'heatmap', toolbar: { show: false } },
      dataLabels: { enabled: false },
      xaxis: { type: 'category' },
      colors: ['#F1F1F1'],
      plotOptions: {
        heatmap: {
          shadeIntensity: 0.4,
          colorScale: {
            ranges: [
              { from: 0, to: 20, color: '#E9F3FF' },
              { from: 21, to: 40, color: '#CFE5FF' },
              { from: 41, to: 60, color: '#A9D0FF' },
              { from: 61, to: 80, color: '#7FBAFF' },
              { from: 81, to: 120, color: '#3E97FF' },
            ],
          },
        },
      },
      legend: { show: false },
    }),
    []
  )
  const heatmapSeries = useMemo(
    () => [
      {
        name: '00-04',
        data: [
          { x: 'Mon', y: 20 },
          { x: 'Tue', y: 18 },
          { x: 'Wed', y: 15 },
          { x: 'Thu', y: 14 },
          { x: 'Fri', y: 22 },
          { x: 'Sat', y: 30 },
          { x: 'Sun', y: 28 },
        ],
      },
      {
        name: '04-08',
        data: [
          { x: 'Mon', y: 40 },
          { x: 'Tue', y: 42 },
          { x: 'Wed', y: 38 },
          { x: 'Thu', y: 35 },
          { x: 'Fri', y: 48 },
          { x: 'Sat', y: 55 },
          { x: 'Sun', y: 50 },
        ],
      },
      {
        name: '08-12',
        data: [
          { x: 'Mon', y: 70 },
          { x: 'Tue', y: 72 },
          { x: 'Wed', y: 75 },
          { x: 'Thu', y: 78 },
          { x: 'Fri', y: 82 },
          { x: 'Sat', y: 65 },
          { x: 'Sun', y: 60 },
        ],
      },
      {
        name: '12-16',
        data: [
          { x: 'Mon', y: 90 },
          { x: 'Tue', y: 88 },
          { x: 'Wed', y: 92 },
          { x: 'Thu', y: 95 },
          { x: 'Fri', y: 100 },
          { x: 'Sat', y: 80 },
          { x: 'Sun', y: 75 },
        ],
      },
      {
        name: '16-20',
        data: [
          { x: 'Mon', y: 85 },
          { x: 'Tue', y: 83 },
          { x: 'Wed', y: 80 },
          { x: 'Thu', y: 82 },
          { x: 'Fri', y: 88 },
          { x: 'Sat', y: 70 },
          { x: 'Sun', y: 68 },
        ],
      },
      {
        name: '20-24',
        data: [
          { x: 'Mon', y: 55 },
          { x: 'Tue', y: 52 },
          { x: 'Wed', y: 50 },
          { x: 'Thu', y: 48 },
          { x: 'Fri', y: 60 },
          { x: 'Sat', y: 58 },
          { x: 'Sun', y: 54 },
        ],
      },
    ],
    []
  )

  return (
    <div className='container-fluid'>
      <div className='card mb-10'>
        <div className='card-body p-10'>
          <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-5'>
            <div>
              <h2 className='fw-bold mb-2'>Profiling</h2>
              <div className='text-muted'>
                Analyze company energy consumption profiles over selected periods.
              </div>
            </div>
            <div className='d-flex align-items-center gap-3' style={{ minWidth: 320 }}>
              <div className='flex-grow-1'>
                <Select<Option, false>
                  classNamePrefix='react-select'
                  placeholder='Select profile'
                  value={selectedProfile}
                  onChange={(opt: SingleValue<Option>) => setSelectedProfile(opt)}
                  options={profileOptions}
                />
              </div>
              <button className='btn btn-primary' onClick={() => setIsModalOpen(true)}>
                Generate Profile
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className='row g-5 mb-5'>
        {kpis.map((kpi) => (
          <div className='col-12 col-md-4' key={kpi.title}>
            <div className='card h-100'>
              <div className='card-body d-flex flex-column justify-content-center'>
                <div className='fw-bold text-gray-600'>{kpi.title}</div>
                <div className='fs-2 fw-bolder mt-2'>{kpi.value}</div>
                <div className='text-muted'>{kpi.change}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className='row g-5'>
        <div className='col-12 col-xl-6'>
          <div className='card h-100'>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bold fs-4'>Weekly Consumption</span>
                <span className='text-muted mt-1 fw-semibold'>Distribution by weekday</span>
              </h3>
            </div>
            <div className='card-body'>
              <ReactApexChart
                options={weeklyOptions}
                series={weeklySeries}
                type='line'
                height={320}
              />
            </div>
          </div>
        </div>
        <div className='col-12 col-xl-6'>
          <div className='card h-100'>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bold fs-4'>Seasonal Distribution</span>
                <span className='text-muted mt-1 fw-semibold'>Share by season</span>
              </h3>
            </div>
            <div className='card-body'>
              <ReactApexChart
                options={seasonalDonutOptions}
                series={seasonalDonutSeries}
                type='donut'
                height={320}
              />
            </div>
          </div>
        </div>
        <div className='col-12'>
          <div className='card'>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bold fs-4'>Daily Patterns</span>
                <span className='text-muted mt-1 fw-semibold'>Peak periods and deviations</span>
              </h3>
            </div>
            <div className='card-body'>
              <ReactApexChart
                options={heatmapOptions}
                series={heatmapSeries}
                type='heatmap'
                height={280}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modal: Generate Profile */}
      <div
        className={`modal fade ${isModalOpen ? 'show d-block' : ''}`}
        tabIndex={-1}
        role='dialog'
      >
        <div className='modal-dialog modal-lg' role='document'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>Generate Profile</h5>
              <button
                type='button'
                className='btn-close'
                aria-label='Close'
                onClick={() => setIsModalOpen(false)}
              />
            </div>
            <div className='modal-body'>
              <div className='row g-5'>
                <div className='col-md-6'>
                  <label className='form-label'>Start date</label>
                  <input
                    type='date'
                    className='form-control'
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className='col-md-6'>
                  <label className='form-label'>End date</label>
                  <input
                    type='date'
                    className='form-control'
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                <div className='col-12'>
                  <label className='form-label'>Events (CSV import)</label>
                  <input
                    type='file'
                    accept='.csv'
                    className='form-control'
                    onChange={(e) => setEventsFile(e.target.files?.[0] ?? null)}
                  />
                  {eventsFile ? (
                    <div className='form-text'>Uploaded: {eventsFile.name}</div>
                  ) : (
                    <div className='form-text'>event_name,event_date,event_weight</div>
                  )}
                </div>
                <div className='col-12'>
                  <button
                    type='button'
                    className='btn btn-light-primary'
                    onClick={handleDownloadEventsTemplate}
                  >
                    Download events CSV format
                  </button>
                </div>
              </div>
            </div>
            <div className='modal-footer'>
              <button type='button' className='btn btn-light' onClick={() => setIsModalOpen(false)}>
                Close
              </button>
              <button
                type='button'
                className='btn btn-primary'
                onClick={handleGenerateProfile}
                disabled={!startDate || !endDate}
              >
                Generate
              </button>
            </div>
          </div>
        </div>
      </div>
      {isModalOpen && (
        <div className='modal-backdrop fade show' onClick={() => setIsModalOpen(false)} />
      )}
    </div>
  )
}

export default ProfilingPage
